import Phaser from 'phaser'
import { IS_MOBILE } from '../main'

export class MenuScene extends Phaser.Scene {
  private joinCodeInput: string = ''
  private joinCodeText!: Phaser.GameObjects.Text
  private joinPanel!: Phaser.GameObjects.Container
  private errorText!: Phaser.GameObjects.Text
  private isMobile: boolean = IS_MOBILE

  constructor() {
    super({ key: 'MenuScene' })
  }

  create() {
    const width = this.cameras.main.width
    const height = this.cameras.main.height
    this.joinCodeInput = ''

    // Background pattern
    const graphics = this.add.graphics()
    graphics.fillStyle(0x0a0a15)
    graphics.fillRect(0, 0, width, height)

    // Grid pattern in background
    graphics.lineStyle(1, 0x1a1a2e, 0.5)
    for (let x = 0; x < width; x += 40) {
      graphics.lineBetween(x, 0, x, height)
    }
    for (let y = 0; y < height; y += 40) {
      graphics.lineBetween(0, y, width, y)
    }

    // Title
    const title = this.add.text(width / 2, this.isMobile ? 80 : 120, 'LINE TOWER WARS', {
      fontSize: this.isMobile ? '40px' : '56px',
      fontFamily: 'monospace',
      color: '#4a90d9',
      fontStyle: 'bold'
    })
    title.setOrigin(0.5)

    // Subtitle
    const subtitle = this.add.text(width / 2, this.isMobile ? 125 : 180, 'Build. Defend. Conquer.', {
      fontSize: this.isMobile ? '14px' : '18px',
      fontFamily: 'monospace',
      color: '#666666'
    })
    subtitle.setOrigin(0.5)

    // Version
    this.add.text(width / 2, this.isMobile ? 150 : 210, 'v0.4.0', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#444444'
    }).setOrigin(0.5)

    // Single Player Button
    this.createButton(
      width / 2,
      this.isMobile ? 220 : 300,
      'SINGLE PLAYER',
      '#00ff88',
      'Play against AI opponent',
      () => this.scene.start('GameScene', { mode: 'single' })
    )

    // Create Room Button
    this.createButton(
      this.isMobile ? width / 2 : width / 2 - 150,
      this.isMobile ? 300 : 380,
      'CREATE ROOM',
      '#4a90d9',
      'Host a multiplayer game',
      () => this.scene.start('LobbyScene', { action: 'create' })
    )

    // Join Room Button
    this.createButton(
      this.isMobile ? width / 2 : width / 2 + 150,
      this.isMobile ? 380 : 380,
      'JOIN ROOM',
      '#d94ad9',
      'Join with room code',
      () => this.showJoinPanel()
    )

    // Create join panel (hidden by default)
    this.createJoinPanel()

    // Instructions panel (smaller on mobile)
    const instructionsY = this.isMobile ? 460 : 480
    const panelWidth = this.isMobile ? 500 : 600
    const panelHeight = this.isMobile ? 100 : 140

    this.add.rectangle(width / 2, instructionsY + panelHeight / 2, panelWidth, panelHeight, 0x1a1a2e)
      .setStrokeStyle(1, 0x2a2a3e)

    this.add.text(width / 2, instructionsY + 15, 'HOW TO PLAY', {
      fontSize: this.isMobile ? '12px' : '14px',
      fontFamily: 'monospace',
      color: '#4a90d9',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    const instructions = this.isMobile ? [
      'Tap towers to select, tap grid to place.',
      'Send units to attack your opponent!',
      'Last player standing wins!'
    ] : [
      '[1-4] Select towers    [Q-R] Send units    [SPACE] Ready',
      '',
      'Build towers to defend your lane from creeps.',
      'Send units to attack your opponent and increase your income.',
      'Last player standing wins!'
    ]

    this.add.text(width / 2, instructionsY + (this.isMobile ? 35 : 45), instructions.join('\n'), {
      fontSize: this.isMobile ? '11px' : '13px',
      fontFamily: 'monospace',
      color: '#888888',
      align: 'center',
      lineSpacing: this.isMobile ? 4 : 6
    }).setOrigin(0.5, 0)

    // Keyboard shortcut hint (only on desktop)
    if (!this.isMobile) {
      this.add.text(width / 2, height - 30, 'Press ENTER to start single player', {
        fontSize: '14px',
        fontFamily: 'monospace',
        color: '#444444'
      }).setOrigin(0.5)
    }

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-ENTER', () => {
      if (!this.joinPanel.visible) {
        this.scene.start('GameScene', { mode: 'single' })
      }
    })

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.joinPanel.visible) {
        this.hideJoinPanel()
      }
    })

    // Handle keyboard input for join code
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.joinPanel.visible) return

      if (event.key === 'Backspace') {
        this.joinCodeInput = this.joinCodeInput.slice(0, -1)
        this.updateJoinCodeDisplay()
      } else if (event.key === 'Enter' && this.joinCodeInput.length >= 4) {
        this.joinRoom()
      } else if (event.key.length === 1 && this.joinCodeInput.length < 6) {
        const char = event.key.toUpperCase()
        if (/[A-Z0-9]/.test(char)) {
          this.joinCodeInput += char
          this.updateJoinCodeDisplay()
        }
      }
    })
  }

  private createButton(
    x: number,
    y: number,
    text: string,
    color: string,
    description: string,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y)
    const buttonWidth = this.isMobile ? 280 : 260
    const buttonHeight = this.isMobile ? 60 : 55

    const bg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x1a1a2e)
    bg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color)
    bg.setInteractive({ useHandCursor: true })

    const label = this.add.text(0, -5, text, {
      fontSize: this.isMobile ? '18px' : '20px',
      fontFamily: 'monospace',
      color: color,
      fontStyle: 'bold'
    })
    label.setOrigin(0.5)

    const desc = this.add.text(0, 18, description, {
      fontSize: this.isMobile ? '10px' : '11px',
      fontFamily: 'monospace',
      color: '#666666'
    })
    desc.setOrigin(0.5)

    container.add([bg, label, desc])

    bg.on('pointerover', () => {
      bg.setFillStyle(0x252535)
      bg.setStrokeStyle(3, Phaser.Display.Color.HexStringToColor(color).color)
    })

    bg.on('pointerout', () => {
      bg.setFillStyle(0x1a1a2e)
      bg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color)
    })

    bg.on('pointerdown', onClick)

    return container
  }

  private createJoinPanel() {
    const width = this.cameras.main.width
    const height = this.cameras.main.height

    this.joinPanel = this.add.container(width / 2, height / 2)
    this.joinPanel.setVisible(false)
    this.joinPanel.setDepth(100)

    // Overlay
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.8)
    overlay.setInteractive()
    overlay.on('pointerdown', () => this.hideJoinPanel())

    // Panel
    const panel = this.add.rectangle(0, 0, 400, 250, 0x1a1a2e)
    panel.setStrokeStyle(2, 0xd94ad9)
    panel.setInteractive() // Prevent clicks from closing

    // Title
    const title = this.add.text(0, -90, 'JOIN ROOM', {
      fontSize: '24px',
      fontFamily: 'monospace',
      color: '#d94ad9',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // Instructions
    const hint = this.add.text(0, -55, 'Enter the 6-digit room code', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#888888'
    }).setOrigin(0.5)

    // Code input display
    const inputBg = this.add.rectangle(0, 0, 300, 60, 0x0a0a15)
    inputBg.setStrokeStyle(2, 0x3a3a4e)

    this.joinCodeText = this.add.text(0, 0, '______', {
      fontSize: '36px',
      fontFamily: 'monospace',
      color: '#ffffff',
      letterSpacing: 8
    }).setOrigin(0.5)

    // Error text
    this.errorText = this.add.text(0, 45, '', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#ff4444'
    }).setOrigin(0.5)

    // Join button
    const joinBtn = this.add.rectangle(0, 90, 150, 45, 0x2a5a2a)
    joinBtn.setStrokeStyle(2, 0x3a8a3a)
    joinBtn.setInteractive({ useHandCursor: true })

    const joinBtnText = this.add.text(0, 90, 'JOIN', {
      fontSize: '18px',
      fontFamily: 'monospace',
      color: '#00ff88',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    joinBtn.on('pointerover', () => joinBtn.setFillStyle(0x3a7a3a))
    joinBtn.on('pointerout', () => joinBtn.setFillStyle(0x2a5a2a))
    joinBtn.on('pointerdown', () => this.joinRoom())

    // Cancel hint
    const cancelHint = this.add.text(170, -100, 'ESC', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#666666'
    }).setOrigin(0.5)

    this.joinPanel.add([overlay, panel, title, hint, inputBg, this.joinCodeText, this.errorText, joinBtn, joinBtnText, cancelHint])
  }

  private showJoinPanel() {
    this.joinCodeInput = ''
    this.updateJoinCodeDisplay()
    this.errorText.setText('')
    this.joinPanel.setVisible(true)
  }

  private hideJoinPanel() {
    this.joinPanel.setVisible(false)
  }

  private updateJoinCodeDisplay() {
    const display = this.joinCodeInput.padEnd(6, '_')
    this.joinCodeText.setText(display.split('').join(' '))
  }

  private joinRoom() {
    if (this.joinCodeInput.length < 4) {
      this.errorText.setText('Code must be at least 4 characters')
      return
    }

    this.hideJoinPanel()
    this.scene.start('LobbyScene', {
      action: 'join',
      roomId: this.joinCodeInput
    })
  }
}
